import React, { useEffect, useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import CreateMarketingActivityFilter from '../../filters/CreateMarketingActivityFilterNew';
import CenterSpinner from '../../components/CenterSpinner';
import {
  CreateMarketActivityFilterType,
  EmployeeResultType,
  LocationFilterInitialState,
  MapOptionType,
  SelectOptionType,
  SetMarketActivityFilterType,
  SuggestionsType,
} from '../../utils/Types';
import MSelect from '../../components/MSelect';
import MDatePicker from '../../components/MDatePicker';
import { MARKET_ACTIVITY_FILTER_INIT } from '../../utils/Constant';
import { AssigneeColumnHeader, moduleIds, listTypes } from './activityHelper';
import MarketingActivitySucessModal from '../modals/MarketingActivitySuccessModal';
import MAutoCompleteMultiSelect from '../../components/MAutoCompleteMultiSelect';

import { useAppDispatch, useAppSelector } from '../../store/redux-hooks';
import {
  createMarketingActivity,
  fetchTargetDrops,
  getEmployeeList,
  getMarketingActivityDetails,
  marketingActivitySelector,
} from '../../features/marketingActivitySlice';
import { toast } from 'react-toastify';
import moment from 'moment';
import { isAPIFailed, isAPISuccess } from '../../utils/Util';
import {
  fetchVillagesOfDistricts,
  locationFilterSelector,
} from '../../features/locationFilterSlice';
import ToggleSwitch from '../../components/ToggleSwitch';
import classNames from 'classnames';
import KTable from '../../components/KTable';
import { Image } from 'react-bootstrap';
import Calendar from '../../assets/img/calendar.svg';

const CreateMarketingActivityPage: React.FunctionComponent = () => {
  const [stateOption, setStateOption] = useState<SelectOptionType[]>([]);
  const allStates: MapOptionType = useAppSelector(
    locationFilterSelector
  ).statesData;
  const zoneOption: SelectOptionType[] = useAppSelector(
    locationFilterSelector
  ).zoneOptions;

  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [selectedEmployees, setSelectedEmployees] = useState<SuggestionsType[]>(
    []
  );
  const [selectedEmployeesDetails, setSelectedEmployeesDetails] = useState<
    EmployeeResultType[]
  >([]);
  const employeeList = useAppSelector(marketingActivitySelector).employeeList;
  const marketingTypes = useAppSelector(
    marketingActivitySelector
  ).marketingTypes;
  const [optionEmployeeName, setOptionEmployeeName] = useState<
    SuggestionsType[] | []
  >([]);
  const [filterValues, setFilterValues] = useState<SetMarketActivityFilterType>(
    MARKET_ACTIVITY_FILTER_INIT
  );
  const [filterData, setFilterData] =
    useState<CreateMarketActivityFilterType>();
  const [primaryEmpUniqueId, setPrimaryEmpUniqueId] = useState<any>();
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [showAssignees, setShowAssignees] = useState<boolean>(false);
  const [createdActivityId, setCreatedActivityId] = useState<string | number>();
  const [createdActivity, setCreatedActivity] = useState<any>();
  const [activityDate, setActivityDate] = useState(
    moment(new Date()).add(10, 'minutes').format('DD-MM-YYYY H:mm A')
  );
  const [showFilter, setShowFilter] = useState<boolean>(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchTargetDrops({ module: moduleIds.marketingTypes }));
  }, []);

  useEffect(() => {
    if (!showAssignees) {
      setSelectedEmployees([]);
    } else {
      setActivityDate(
        moment(new Date()).add(10, 'minutes').format('DD-MM-YYYY H:mm A')
      );
    }
  }, [showAssignees]);

  useEffect(() => {
    const employeeOptions: SuggestionsType[] = [];
    employeeList.map((emp) => {
      employeeOptions.push({
        label: emp.employeeName,
        value: emp.employeeId,
        id: emp.employeeUniqueId,
      });
    });
    setOptionEmployeeName(employeeOptions);
  }, [employeeList]);

  useEffect(() => {
    const empsObj: EmployeeResultType[] = [];
    if (selectedEmployees.length) {
      const selectedUniqueIds: (string | number)[] = selectedEmployees.map(
        (option) => option.value
      );

      for (const element of employeeList) {
        if (selectedUniqueIds.includes(element.employeeId)) {
          empsObj.push(element);
        }
      }
      if (!selectedUniqueIds.includes(primaryEmpUniqueId)) {
        setPrimaryEmpUniqueId(null);
      }
    } else if (primaryEmpUniqueId) {
      setPrimaryEmpUniqueId(null);
    }
    setSelectedEmployeesDetails(empsObj);
  }, [selectedEmployees]);

  useEffect(() => {
    if (createdActivityId) {
      setShowLoader(true);
      dispatch(
        getMarketingActivityDetails({ activityUniqueId: createdActivityId })
      )
        .unwrap()
        .then((fulfilled: any) => {
          setShowLoader(false);
          if (isAPIFailed(fulfilled)) {
            toast.error(fulfilled?.responseInformation.responseMessage);
          } else if (isAPISuccess(fulfilled)) {
            setCreatedActivity(fulfilled.result);
          }
        })
        .catch(() => {
          setShowLoader(false);
          toast.error('Fetching Marketing Activities Failed!');
        });
    }
  }, [createdActivityId]);
  const locationData: LocationFilterInitialState = useAppSelector(
    locationFilterSelector
  );

  useEffect(() => {
    if (employeeList?.length) {
      const districtIds: number[] = Array.from(
        new Set(
          employeeList.map((employee: any) => Number(employee.districtId))
        )
      );
      const existingDistrictVillages = Object.keys(
        locationData.cityOptions
      ).map((key) => Number(key));
      const districtsWithNoVillages = districtIds.filter(
        (id) => !existingDistrictVillages.includes(id)
      );
      if (districtsWithNoVillages.length) {
        dispatch(
          fetchVillagesOfDistricts({
            districtId: districtsWithNoVillages,
          })
        );
      }
    }
  }, [employeeList]);

  const onSubmitFilter = (isValid: boolean) => {
    if (isValid) {
      setShowAssignees(true);
      setFilterData((prev: any) => ({
        ...prev,
        ...filterValues,
      }));
      dispatch(
        getEmployeeList({
          zoneId: filterValues.zone,
          stateId: filterValues.state,
        })
      );
      setSelectedEmployees([]);
    } else {
      setShowAssignees(false);
      setFilterData(MARKET_ACTIVITY_FILTER_INIT);
    }
  };
  const handleReset = () => {
    const newFilter = { ...MARKET_ACTIVITY_FILTER_INIT };
    if (stateOption && stateOption.length === 1) {
      newFilter.state = stateOption[0].value;
    }
    if (zoneOption && zoneOption.length === 1) {
      newFilter.zone = zoneOption[0].value;
    }
    setFilterValues(newFilter);
    setFilterData(newFilter);
    setShowAssignees(false);
    setPrimaryEmpUniqueId(null);
  };

  const handleModalClose = () => {
    handleReset();
    setOpenSuccessModal((prevState) => !prevState);
  };

  const createObj = () => {
    const selectedEmployeesArr: any[] = [];
    selectedEmployees.map((emp) => {
      selectedEmployeesArr.push({
        candidateId: emp.value,
        isPrimary: emp.value === primaryEmpUniqueId,
      });
    });

    return {
      financialYear: filterData?.year,
      season: filterData?.season,
      zone: filterData?.zone,
      state: Number(filterData?.state),
      city: Number(filterData?.village),
      district: Number(filterData?.district),
      listOfEmployees: selectedEmployeesArr,
      activityTypeId: filterData?.marketingActivityType,
      activityStartDate: moment(
        activityDate,
        'DD-MM-YYYY H:mm A'
      ).toISOString(),
      comments: filterData?.comment ?? '',
      activityEndDate: new Date(
        moment(activityDate, 'DD-MM-YYYY H:mm').format('DD MMM YYYY H:mm')
      ).toISOString(),
      pincode: filterData?.pincode,
    };
  };

  const handleCreate = () => {
    let isValid = true;
    const isInPast = moment(activityDate, 'DD-MM-YYYY H:mm A').isBefore(
      moment().startOf('minute')
    );

    if (!selectedEmployees.length) {
      toast.error('Please assign the employees');
      isValid = false;
    } else if (!filterData?.marketingActivityType) {
      toast.error('Type of Marketing Activity is required');
      isValid = false;
    } else if (!activityDate) {
      toast.error('Activation Date & Time  is required');
      isValid = false;
    } else if (isInPast) {
      toast.error('Activity Date & Time should be in future');
      isValid = false;
    } else if (!primaryEmpUniqueId) {
      toast.error('Primary employee is required');
      isValid = false;
    }
    if (isValid) {
      setShowLoader(true);
      dispatch(createMarketingActivity(createObj()))
        .unwrap()
        .then((fulfilled: any) => {
          setShowLoader(false);
          if (isAPIFailed(fulfilled)) {
            toast.error(fulfilled?.responseInformation.responseMessage);
          } else if (isAPISuccess(fulfilled)) {
            setCreatedActivityId(fulfilled.result);
            setOpenSuccessModal(true);
          }
        })
        .catch(() => {
          setShowLoader(false);
          toast.error('Marketing Activity creation failed!');
        });
    }
  };

  const handleToggleSwitch = () => {
    setShowFilter((prev) => !prev);
  };
  useEffect(() => {
    if (zoneOption && zoneOption.length === 1) {
      const stateArray = allStates[zoneOption[0].value];
      setStateOption(stateArray);
      const newFilter = { ...filterValues, zone: zoneOption[0].value };
      if (stateArray?.length === 1) {
        newFilter.state = stateArray[0].value;
      }
      setFilterValues(newFilter);
    }
  }, [zoneOption]);
  return (
    <div className="ml-4">
      <Label className="text-heading">Create Sales Activity</Label>
      <ToggleSwitch isOpen={showFilter} onClick={handleToggleSwitch} />
      <div>
        {showFilter && (
          <CreateMarketingActivityFilter
            filterValues={filterValues}
            onReset={handleReset}
            setFilterValues={setFilterValues}
            listType={listTypes.create}
            onSubmitFilter={(isValid?: boolean) => {
              onSubmitFilter(isValid ?? false);
            }}
          />
        )}

        {showAssignees && (
          <div
            className={classNames(
              'mt-3 container-parent parent-table create-marketing',
              {
                hideFilters: !showFilter,
              }
            )}
          >
            <div className="marketing-inner-div">
              <Label className="header-style">Assigned To</Label>
              <span className="text-danger">{` * `}</span>
              <span className="subtitle">
                (You can assign upto 6 employees)
              </span>
              <Row className="mt-2">
                <Col xs={12} md={4}>
                  <MAutoCompleteMultiSelect
                    placeholder="Employee Name"
                    suggestions={optionEmployeeName}
                    value={selectedEmployees}
                    onChange={setSelectedEmployees}
                    noSuggestionsText={'No options'}
                    maxSelectable={6}
                  />
                </Col>
              </Row>

              <KTable
                data={selectedEmployeesDetails}
                tableParentClass="table-parent-assign-employee mt-2"
                columnHeader={AssigneeColumnHeader}
                isLoading={showLoader}
                primaryEmpUniqueId={primaryEmpUniqueId}
                onAction={(emp) => {
                  setPrimaryEmpUniqueId(emp.employeeId);
                }}
                locationData={locationData}
                showNoDataFoundMsg={false}
                showHeader
              />
            </div>

            <div className="mt-3">
              <div className="marketing-inner-div">
                <Label className="header-style">Type of Sales Activities</Label>
                <span className="text-danger">{` * `}</span>
                <Row>
                  <Col xs={6} md={4}>
                    <MSelect
                      label="Type of Sales Activity"
                      options={marketingTypes}
                      value={filterData?.marketingActivityType}
                      onChange={(e) => {
                        setFilterData((prev: any) => ({
                          ...prev,
                          marketingActivityType: e.target.value,
                        }));
                      }}
                      dataTestId="type"
                      hideLabel={true}
                    />
                  </Col>
                </Row>
              </div>
            </div>
            <Row className="d-flex flex-grow-1">
              <Col xs={12} md={6}>
                <div className="date-activity date-activity-left">
                  <Label className="header-style">Activity Date & Time</Label>
                  <span className="text-danger">{` * `}</span>
                  <Row className="mt-2 mb-4">
                    <Col>
                      <MDatePicker
                        label=""
                        value={activityDate}
                        onChange={(e) => {
                          setActivityDate(e);
                        }}
                        disablePast
                        format="DD-MM-YYYY hh:mm A"
                        icon={<Image src={Calendar}></Image>}
                        maxDate={moment().add(90, 'days')}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col>
                <div className="date-activity date-activity-right pb-1">
                  <div>
                    <Label for="comment" className="header-style mb-0">
                      Comments
                    </Label>
                    <FormGroup>
                      <Input
                        id="comment"
                        name="comment"
                        type="textarea"
                        maxLength={250}
                        value={filterData?.comment}
                        placeholder="Please enter comments"
                        className="rounded border model-comment-area"
                        onChange={(e) => {
                          if (e.target.value?.length <= 250)
                            setFilterData((prev: any) => ({
                              ...prev,
                              comment: e.target.value,
                            }));
                        }}
                      />
                    </FormGroup>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center mt-4 mb-2">
              <Button
                variant="primary"
                className="cl-btn-primary mt-2"
                disabled={showLoader}
                onClick={handleCreate}
              >
                Create
              </Button>
              <Button
                variant="primary-outline"
                className="mt-2 mx-4 cl-btn-outline-primary "
                onClick={handleReset}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      <CenterSpinner showLoader={showLoader} className="spinner-60" />

      <MarketingActivitySucessModal
        isOpen={openSuccessModal}
        toggleSuccessModal={() => handleModalClose()}
        type={listTypes.create}
        activityDetails={createdActivity}
      />
    </div>
  );
};

export default CreateMarketingActivityPage;
